import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Copy,
  Save,
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

// Mock data - substituir com dados reais da API
const mockOriginalBudget = {
  id: "1",
  number: "ORC-2024-001",
  client: {
    id: "1",
    name: "Empresa ABC Ltda",
    document: "12.345.678/0001-90"
  },
  items: [
    {
      id: "1",
      description: "Consultoria em TI - Planejamento Estratégico",
      quantity: 40,
      unitPrice: 500.00,
      discount: 10,
      total: 18000.00
    },
    {
      id: "2",
      description: "Desenvolvimento Web - Sistema Interno",
      quantity: 80,
      unitPrice: 150.00,
      discount: 5,
      total: 11400.00
    }
  ]
};

const mockClients = [
  { id: "1", name: "Empresa ABC Ltda", document: "12.345.678/0001-90" },
  { id: "2", name: "Serviços XYZ", document: "98.765.432/0001-10" },
  { id: "3", name: "Consultoria & Cia", document: "45.678.901/0001-23" }
];

interface DuplicateOptions {
  keepClient: boolean;
  keepItems: boolean;
  keepValues: boolean;
  keepDiscounts: boolean;
  adjustPrices: boolean;
  priceAdjustment: number;
}

export default function BudgetDuplicatePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [newBudgetNumber, setNewBudgetNumber] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(mockOriginalBudget.client.id);
  const [validUntil, setValidUntil] = useState("");
  const [options, setOptions] = useState<DuplicateOptions>({
    keepClient: true,
    keepItems: true,
    keepValues: true,
    keepDiscounts: true,
    adjustPrices: false,
    priceAdjustment: 0
  });

  const [duplicatedItems, setDuplicatedItems] = useState(mockOriginalBudget.items);

  useEffect(() => {
    // Gerar número do novo orçamento
    const today = new Date();
    const year = today.getFullYear();
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setNewBudgetNumber(`ORC-${year}-${randomNum}`);

    // Definir validade padrão (30 dias a partir de hoje)
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 30);
    setValidUntil(futureDate.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    // Recalcular valores dos itens baseado nas opções
    const updatedItems = mockOriginalBudget.items.map(item => {
      let newUnitPrice = item.unitPrice;
      let newDiscount = item.discount;

      if (!options.keepValues) {
        newUnitPrice = 0;
      }

      if (!options.keepDiscounts) {
        newDiscount = 0;
      }

      if (options.adjustPrices && options.keepValues) {
        newUnitPrice = item.unitPrice * (1 + options.priceAdjustment / 100);
      }

      const newTotal = (newUnitPrice * item.quantity) * (1 - newDiscount / 100);

      return {
        ...item,
        unitPrice: newUnitPrice,
        discount: newDiscount,
        total: newTotal
      };
    });

    setDuplicatedItems(updatedItems);
  }, [options]);

  const calculateTotals = () => {
    const subtotal = duplicatedItems.reduce((sum, item) => sum + item.total, 0);
    const tax = subtotal * 0.12; // 12% de impostos
    const finalTotal = subtotal + tax;
    return { subtotal, tax, finalTotal };
  };

  const { subtotal, tax, finalTotal } = calculateTotals();

  const handleDuplicate = () => {
    // Implementar lógica de duplicação
    const duplicatedBudget = {
      number: newBudgetNumber,
      clientId: selectedClientId,
      validUntil,
      items: options.keepItems ? duplicatedItems : [],
      options
    };

    console.log("Duplicating budget:", duplicatedBudget);

    // Redirecionar para o novo orçamento
    navigate("/services/budgets");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Copy className="h-6 w-6" />
            Duplicar Orçamento
          </h1>
          <p className="text-muted-foreground">
            Duplicando o orçamento {mockOriginalBudget.number} para {mockOriginalBudget.client.name}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={`/services/budgets/${id}`}>
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </Link>
          <Button onClick={handleDuplicate}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicar Orçamento
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Original Budget Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Orçamento Original
            </CardTitle>
            <CardDescription>
              Informações do orçamento que está sendo duplicado
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-muted-foreground">Número</Label>
                <p className="font-medium">{mockOriginalBudget.number}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Cliente</Label>
                <p className="font-medium">{mockOriginalBudget.client.name}</p>
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Itens ({mockOriginalBudget.items.length})</Label>
              <div className="mt-2 space-y-1">
                {mockOriginalBudget.items.map((item, index) => (
                  <div key={item.id} className="text-sm bg-muted/50 p-2 rounded">
                    <p className="font-medium">{item.description}</p>
                    <p className="text-muted-foreground">
                      {item.quantity} × {item.unitPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      {item.discount > 0 && ` (${item.discount}% desc.)`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Budget Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Configurações do Novo Orçamento
            </CardTitle>
            <CardDescription>
              Defina as informações do novo orçamento
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="budgetNumber">Número do Orçamento</Label>
                <Input
                  id="budgetNumber"
                  value={newBudgetNumber}
                  onChange={(e) => setNewBudgetNumber(e.target.value)}
                  placeholder="ORC-2024-XXX"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="validUntil">Validade</Label>
                <Input
                  id="validUntil"
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client">Cliente</Label>
              <Select
                value={selectedClientId}
                onValueChange={setSelectedClientId}
                disabled={options.keepClient}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um cliente" />
                </SelectTrigger>
                <SelectContent>
                  {mockClients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      <div>
                        <div className="font-medium">{client.name}</div>
                        <div className="text-sm text-muted-foreground">{client.document}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label>Opções de Duplicação</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keepClient"
                    checked={options.keepClient}
                    onCheckedChange={(checked) =>
                      setOptions(prev => ({ ...prev, keepClient: checked as boolean }))
                    }
                  />
                  <Label htmlFor="keepClient" className="text-sm">
                    Manter o mesmo cliente
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keepItems"
                    checked={options.keepItems}
                    onCheckedChange={(checked) =>
                      setOptions(prev => ({ ...prev, keepItems: checked as boolean }))
                    }
                  />
                  <Label htmlFor="keepItems" className="text-sm">
                    Manter todos os itens
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keepValues"
                    checked={options.keepValues}
                    onCheckedChange={(checked) =>
                      setOptions(prev => ({ ...prev, keepValues: checked as boolean }))
                    }
                  />
                  <Label htmlFor="keepValues" className="text-sm">
                    Manter valores unitários
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keepDiscounts"
                    checked={options.keepDiscounts}
                    onCheckedChange={(checked) =>
                      setOptions(prev => ({ ...prev, keepDiscounts: checked as boolean }))
                    }
                  />
                  <Label htmlFor="keepDiscounts" className="text-sm">
                    Manter descontos
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="adjustPrices"
                    checked={options.adjustPrices}
                    onCheckedChange={(checked) =>
                      setOptions(prev => ({ ...prev, adjustPrices: checked as boolean }))
                    }
                  />
                  <Label htmlFor="adjustPrices" className="text-sm">
                    Ajustar preços em
                  </Label>
                  <Input
                    type="number"
                    value={options.priceAdjustment}
                    onChange={(e) =>
                      setOptions(prev => ({ ...prev, priceAdjustment: Number(e.target.value) }))
                    }
                    className="w-20 h-8"
                    disabled={!options.adjustPrices}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview */}
      {options.keepItems && (
        <Card>
          <CardHeader>
            <CardTitle>Prévia dos Itens do Novo Orçamento</CardTitle>
            <CardDescription>
              Visualização dos itens que serão incluídos no novo orçamento
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead className="text-right">Valor Unit.</TableHead>
                  <TableHead className="text-right">Desconto</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {duplicatedItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">
                      {item.unitPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.discount > 0 ? (
                        <Badge variant="secondary">{item.discount}%</Badge>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {item.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>{subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
              </div>
              <div className="flex justify-between">
                <span>Impostos (12%):</span>
                <span>{tax.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t">
                <span>Total Final:</span>
                <span className="text-primary">
                  {finalTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}